import { captureException } from '@sentry/react';
import { parse, stringify } from 'flatted';

import { useLogStoreContext } from 'lib/common/contexts/LogStoreContext';

import { usePreferencesContext } from '../../contexts/PreferencesContext';
import { TLogEventValue } from './LogEvents';
import usePerformanceHeartbeat from './hooks/usePerformanceHeartbeat';
import { transformLogObject } from './utils/transformLogObject';

const logLevels = ['debug', 'info', 'warn', 'error'] as const;

type LogObject = { error?: any; [k: string]: any };

export type TLogger = Record<(typeof logLevels)[number], (logMessage: TLogEventValue, logObject?: LogObject) => void>;

const dummyLogger = (add: (arg0: string) => void) => ({
  ...logLevels.reduce(
    (acc, level) => ({
      ...acc,
      [level]: (...args) => {
        try {
          add(transformLogObject({ logMessage: args[0], logObject: args[1], level }));
          console[level](args[0], args[1] ?? '');
        } catch (e) {
          // Catch any errors thrown to prevent logging from breaking the app
          captureException(e);
        }
      },
    }),
    {} as TLogger
  ),
});

const actualLogger = (add: (arg0: string) => void) =>
  logLevels.reduce(
    (acc, level) => ({
      ...acc,
      [level]: (logMessage: TLogEventValue, logObject?: LogObject) => {
        try {
          add(transformLogObject({ logMessage, logObject, level }));
          console[level](logMessage, logObject ?? '');

          if (!connect) {
            return;
          }

          const log = connect.getLog()[level](`[NEONNOW] %s`, logMessage);

          if (logObject) {
            const { error, ...rest } = logObject;

            if (error) {
              const objectifiedError =
                error instanceof Error
                  ? {
                      name: error?.name,
                      message: error?.message,
                      stack: error?.stack,
                    }
                  : error;

              // Objectifying the error to avoid issues with circular references
              log.withObject(parse(stringify(objectifiedError)));
            }

            // Objectifying the error to avoid issues with circular references
            log.withObject(parse(stringify(rest)));
          }
        } catch (e) {
          // Catch any errors thrown to prevent logging from breaking the app
          captureException(e);
        }
      },
    }),
    {} as TLogger
  );

let logger = dummyLogger(() => {});

function LoggerController({ children }) {
  const {
    state: { enhancedLoggingEnabled, logStreamFilter },
  } = usePreferencesContext();
  const {
    actions: { add },
  } = useLogStoreContext();

  usePerformanceHeartbeat({ enhancedLoggingEnabled });

  // Prevent adding to local logs if all logs are being streamed to CloudWatch
  // https://cloud-wave.atlassian.net/wiki/spaces/PROD/pages/2154790913/What+s+stored+and+what+s+streamed
  const addToLogStore = logStreamFilter === 'ALL' ? () => {} : add;

  const actualLoggerInstance = actualLogger(addToLogStore);
  const dummyLoggerInstance = dummyLogger(addToLogStore);

  logger = enhancedLoggingEnabled
    ? actualLoggerInstance
    : { ...dummyLoggerInstance, error: actualLoggerInstance.error };

  return <>{children}</>;
}

export default LoggerController;
export { logger };
